import React, { useState, useRef } from 'react';
import HrnbFileUpload from '../controls/HrnbFileUpload';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbInput from '../controls/HrnbInput';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbSelect from '../controls/HrnbSelect';
import AudioPlayer from './AudioPlayer';
import '../../css/components/TrackUploader.css';
import { battlesApi, worksApi, textsApi } from '../../api';

/**
 * Компонент загрузки трека в раунд баттла
 * Поддерживает:
 * - Текстовый трек (без аудиофайла)
 * - Аудио трек с загрузкой файла (multipart)
 * - Аудио трек с внешним URL
 *
 * @component
 * @param {Object} props
 * @param {string} props.battleId - UUID баттла
 * @param {string} props.roundId - UUID раунда
 * @param {string} props.authorId - UUID профиля автора (участника)
 * @param {string} props.battlePairId - UUID пары баттла (опционально)
 * @param {Function} props.onSuccess - Колбэк при успешной загрузке
 * @param {Function} props.onCancel - Колбэк при отмене
 * @param {boolean} props.isAdmin - Права администратора
 * @param {import('../../model/battle').BattleFormat} [props.battleFormat] - Формат баттла (AUDIO|TEXT)
 */
export default function TrackUploader({
  battleId,
  roundId,
  authorId,
  battlePairId,
  onSuccess,
  onCancel,
  isAdmin = false,
  battleFormat = 'AUDIO'
}) {
  const [trackType, setTrackType] = useState(battleFormat === 'TEXT' ? 'text' : 'audio'); // 'text' | 'audio'
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'url' (только для audio)
  const [selectedFile, setSelectedFile] = useState(null);
  const [trackUrl, setTrackUrl] = useState('');
  const [trackTitle, setTrackTitle] = useState('');
  const [trackDescription, setTrackDescription] = useState('');
  const [trackText, setTrackText] = useState('');
  const [duration, setDuration] = useState('');
  const [format, setFormat] = useState('MP3');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [limitations, setLimitations] = useState([]);
  const fileInputRef = useRef(null);

  // Загружаем ограничения раунда при открытии
  React.useEffect(() => {
    if (!battleId || !roundId) return undefined;

    let cancelled = false;

    battlesApi
      .getRoundLimitations(battleId, roundId)
      .then((data) => {
        if (!cancelled) {
          const active = (Array.isArray(data) ? data : []).filter((l) => l.isActive !== false);
          setLimitations(active);
        }
      })
      .catch((err) => {
        // Ограничения опциональны — не блокируем загрузку при ошибке
        console.error('[TrackUploader] Failed to load round limitations:', err.message);
        if (!cancelled) setLimitations([]);
      });

    return () => {
      cancelled = true;
    };
  }, [battleId, roundId]);

  const audioFormats = [
    { value: 'MP3', label: 'MP3' },
    { value: 'WAV', label: 'WAV' },
    { value: 'OGG', label: 'OGG' },
    { value: 'FLAC', label: 'FLAC' },
    { value: 'AAC', label: 'AAC' },
    { value: 'M4A', label: 'M4A' }
  ];

  /**
   * Проверка работы на соблюдение ограничений раунда:
   * - TRACK_DURATION: мин/макс длительность трека (сек)
   * - TEXT_CONTENT: мин/макс длина текста, запрещённые слова
   * - FILE_SIZE: максимальный размер файла (МБ)
   * - FILE_FORMAT: разрешённые форматы (строка через запятую)
   * @returns {string|null} Сообщение об ошибке или null, если всё в порядке
   */
  const validateAgainstLimitations = () => {
    for (const lim of limitations) {
      const name = lim.name || 'Ограничение';

      // Длительность трека (только для аудио)
      if (lim.limitationType === 'TRACK_DURATION' && trackType === 'audio') {
        const dur = parseInt(duration, 10);
        if (!isNaN(dur) && dur > 0) {
          if (lim.minTrackDuration != null && dur < lim.minTrackDuration) {
            return `${name}: длительность трека должна быть не менее ${lim.minTrackDuration} сек.`;
          }
          if (lim.maxTrackDuration != null && dur > lim.maxTrackDuration) {
            return `${name}: длительность трека не должна превышать ${lim.maxTrackDuration} сек.`;
          }
        }
      }

      // Текст: длина и запрещённые слова
      if (lim.limitationType === 'TEXT_CONTENT') {
        const text = trackText.trim();
        const len = text.length;
        if (lim.minTextLength != null && len < lim.minTextLength) {
          return `${name}: текст должен содержать не менее ${lim.minTextLength} символов (сейчас ${len}).`;
        }
        if (lim.maxTextLength != null && len > lim.maxTextLength) {
          return `${name}: текст не должен превышать ${lim.maxTextLength} символов (сейчас ${len}).`;
        }
        if (lim.forbiddenWords) {
          const lowerText = text.toLowerCase();
          const forbidden = lim.forbiddenWords
            .split(',')
            .map((w) => w.trim().toLowerCase())
            .filter(Boolean);
          const found = forbidden.find((w) => lowerText.includes(w));
          if (found) {
            return `${name}: текст содержит запрещённое слово «${found}».`;
          }
        }
      }

      // Размер файла (только при загрузке файла)
      if (lim.limitationType === 'FILE_SIZE' && trackType === 'audio' && uploadMode === 'file' && selectedFile) {
        if (lim.maxFileSizeMb != null && selectedFile.size > lim.maxFileSizeMb * 1024 * 1024) {
          return `${name}: размер файла не должен превышать ${lim.maxFileSizeMb} МБ (сейчас ${(selectedFile.size / (1024 * 1024)).toFixed(1)} МБ).`;
        }
      }

      // Разрешённые форматы (только при загрузке файла)
      if (lim.limitationType === 'FILE_FORMAT' && trackType === 'audio' && uploadMode === 'file' && selectedFile) {
        if (lim.allowedFormats) {
          const allowed = lim.allowedFormats
            .split(',')
            .map((f) => f.trim().toUpperCase())
            .filter(Boolean);
          const fileExt = (selectedFile.name.split('.').pop() || '').toUpperCase();
          const fileFormat = (format || '').toUpperCase();
          const formatMatches =
            allowed.includes(fileExt) || allowed.includes(fileFormat);
          if (allowed.length > 0 && !formatMatches) {
            return `${name}: допустимые форматы — ${allowed.join(', ')}.`;
          }
        }
      }
    }

    return null;
  };

  const validateForm = () => {
    if (!trackTitle.trim()) {
      setError('Укажите название трека');
      return false;
    }

    if (!trackText.trim()) {
      setError('Введите текст трека');
      return false;
    }

    // Для аудио-режима дополнительная проверка
    if (trackType === 'audio') {
      if (uploadMode === 'file') {
        if (!selectedFile) {
          setError('Выберите аудиофайл');
          return false;
        }

        // Проверка размера файла (макс 30MB)
        const maxSize = 30 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
          setError(`Размер файла превышает лимит (макс. 30 МБ)`);
          return false;
        }

        // Проверка формата файла
        const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/mp4'];
        if (!allowedTypes.includes(selectedFile.type)) {
          setError(`Неподдерживаемый формат файла. Допустимы: MP3, WAV, OGG, FLAC, AAC, M4A`);
          return false;
        }
      } else {
        if (!trackUrl.trim()) {
          setError('Укажите URL трека');
          return false;
        }

        // Простая валидация URL
        try {
          new URL(trackUrl);
        } catch {
          setError('Некорректный URL');
          return false;
        }
      }

      if (!duration || isNaN(duration) || parseInt(duration) <= 0) {
        setError('Укажите длительность трека в секундах');
        return false;
      }
    }

    // Проверка на соблюдение ограничений раунда
    const limitationError = validateAgainstLimitations();
    if (limitationError) {
      setError(limitationError);
      return false;
    }

    if (!authorId) {
      console.error('[TrackUploader] authorId is missing');
      setError('Не указан автор трека. Убедитесь, что в раунде есть участники.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    console.log('[TrackUploader] handleSubmit called', {
      battleId,
      roundId,
      authorId,
      battlePairId,
      trackType,
      uploadMode,
      trackTitle,
      selectedFile,
      duration
    });

    if (!validateForm()) {
      console.log('[TrackUploader] Validation failed');
      return;
    }

    setLoading(true);

    try {
      console.log('[TrackUploader] Starting upload...', {
        battleId,
        roundId,
        authorId,
        battlePairId,
        trackType,
        uploadMode,
        trackTitle,
        duration,
        format
      });

      if (trackType === 'text') {
        // Текстовый трек - создаём Text, затем Work
        console.log('[TrackUploader] Creating text work...');

        // 1. Создаём текст
        const textData = {
          battlePersonId: authorId,
          title: trackTitle.trim(),
          description: trackDescription.trim() || null,
          content: trackText.trim(),
          status: 'SUBMITTED'
        };

        console.log('[TrackUploader] Creating text:', textData);
        const textResult = await textsApi.createText(textData);
        console.log('[TrackUploader] Text created:', textResult);

        // 2. Создаём работу с текстом
        const workData = {
          battlePersonId: authorId,
          roundId: roundId,
          textId: textResult.id,
          status: 'SUBMITTED'
        };

        console.log('[TrackUploader] Creating work with text:', workData);
        const workResult = await worksApi.createWork(workData);
        console.log('[TrackUploader] Work created successfully:', workResult);

        if (onSuccess) {
          onSuccess(workResult);
        }
      } else if (uploadMode === 'file') {
        // Аудио трек с файлом: multipart/form-data с двумя частями:
        // track (File) и data (JSON UploadTrackRequest)
        const trackMetadata = {
          authorId: authorId,
          battlePairId: battlePairId || null,
          title: trackTitle.trim(),
          description: trackDescription.trim() || null,
          text: trackText.trim() || null,
          duration: parseInt(duration),
          format: format,
          status: 'UPLOADED'
        };

        // Диагностика: text должен быть в data-части (сохраняется в tracks.text)
        if (!trackMetadata.text) {
          console.warn('[TrackUploader] WARNING: text is empty in upload payload!', {
            trackTextLength: trackText.length,
            trackType,
            uploadMode
          });
        }
        console.log('[TrackUploader] Upload data part:', JSON.stringify(trackMetadata));

        const formData = new FormData();
        formData.append('track', selectedFile);
        // Blob с явным Content-Type: application/json — иначе Spring не сможет
        // распарсить @RequestPart data (получит application/octet-stream)
        formData.append(
          'data',
          new Blob([JSON.stringify(trackMetadata)], { type: 'application/json' })
        );

        console.log('[TrackUploader] FormData prepared, calling uploadTrack API...', {
          track: selectedFile.name,
          data: trackMetadata
        });

        // Загружаем трек через API (возвращает WorkResponse)
        const workResult = await battlesApi.uploadTrack(
          battleId,
          roundId,
          formData,
          (percent) => setProgress(percent)
        );
        console.log('[TrackUploader] Work uploaded:', workResult);

        // text из data сохраняется в tracks.text на сервере, но WorkTrackResponse
        // его не возвращает — текст отображается через ленивую загрузку
        // GET /api/tracks/{id} в кнопке «Текст» (RoundsTabs)

        if (onSuccess) {
          onSuccess(workResult);
        }
      } else {
        // Аудио трек с внешним URL
        const payload = {
          title: trackTitle.trim(),
          description: trackDescription.trim(),
          text: trackText.trim(),
          mediaUrl: trackUrl.trim(),
          authorId: authorId,
          duration: parseInt(duration),
          format: format,
          ...(battlePairId && { battlePairId })
        };

        console.log('[TrackUploader] Creating track from URL, payload:', payload);
        // Создаём трек через API (возвращает WorkResponse)
        const workResult = await battlesApi.createTrackByUrl(battleId, roundId, payload);
        console.log('[TrackUploader] Work created:', workResult);

        if (onSuccess) {
          onSuccess(workResult);
        }
      }
    } catch (err) {
      console.error('[TrackUploader] Failed to upload track/work:', err);
      console.error('[TrackUploader] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.message || 'Ошибка при загрузке работы');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setError(null);

      // Создаём URL для превью
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleReset = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setTrackType(battleFormat === 'TEXT' ? 'text' : 'audio');
    setUploadMode('file');
    setSelectedFile(null);
    setTrackUrl('');
    setTrackTitle('');
    setTrackDescription('');
    setTrackText('');
    setDuration('');
    setFormat('MP3');
    setError(null);
    setProgress(0);
    setPreviewUrl(null);
  };

  // Проверка authorId при монтировании
  React.useEffect(() => {
    if (!authorId) {
      console.warn('[TrackUploader] authorId is missing. The component requires a valid authorId to upload tracks.');
    }
  }, [authorId]);

  // Очистка URL при размонтировании
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="track-uploader">
      {/* Переключатель типа трека */}
      <div className="track-uploader__type-toggle">
        <button
          type="button"
          className={`type-btn ${trackType === 'text' ? 'active' : ''}`}
          onClick={() => { setTrackType('text'); handleReset(); }}
          disabled={battleFormat === 'AUDIO'}
        >
          📝 Текстовый трек
        </button>
        <button
          type="button"
          className={`type-btn ${trackType === 'audio' ? 'active' : ''}`}
          onClick={() => { setTrackType('audio'); handleReset(); }}
          disabled={battleFormat === 'TEXT'}
        >
          🎵 Аудио трек
        </button>
      </div>

      <div className="track-uploader__mode-toggle">
        <button
          type="button"
          className={`mode-btn ${uploadMode === 'file' ? 'active' : ''}`}
          onClick={() => { setUploadMode('file'); handleReset(); }}
          disabled={trackType === 'text'}
        >
          📁 Загрузить файл
        </button>
        <button
          type="button"
          className={`mode-btn ${uploadMode === 'url' ? 'active' : ''}`}
          onClick={() => { setUploadMode('url'); handleReset(); }}
          disabled={trackType === 'text'}
        >
          🔗 Внешний URL
        </button>
      </div>

      <form onSubmit={handleSubmit} className="track-uploader__form">
        {error && (
          <HrnbAlert variant="danger" onClose={() => setError(null)}>
            {error}
          </HrnbAlert>
        )}

        {/* Ограничения раунда */}
        {limitations.length > 0 && (
          <HrnbAlert variant="info">
            <div className="track-uploader__limitations">
              <strong>Ограничения раунда:</strong>
              <ul>
                {limitations.map((lim) => {
                  const parts = [];
                  if (lim.limitationType === 'TRACK_DURATION') {
                    if (lim.minTrackDuration != null) parts.push(`от ${lim.minTrackDuration} сек`);
                    if (lim.maxTrackDuration != null) parts.push(`до ${lim.maxTrackDuration} сек`);
                  } else if (lim.limitationType === 'TEXT_CONTENT') {
                    if (lim.minTextLength != null) parts.push(`от ${lim.minTextLength} символов`);
                    if (lim.maxTextLength != null) parts.push(`до ${lim.maxTextLength} символов`);
                    if (lim.forbiddenWords) parts.push(`запрещены: ${lim.forbiddenWords}`);
                  } else if (lim.limitationType === 'FILE_SIZE' && lim.maxFileSizeMb != null) {
                    parts.push(`файл не более ${lim.maxFileSizeMb} МБ`);
                  } else if (lim.limitationType === 'FILE_FORMAT' && lim.allowedFormats) {
                    parts.push(`форматы: ${lim.allowedFormats}`);
                  }
                  return (
                    <li key={lim.id}>
                      {lim.name}
                      {parts.length > 0 ? ` — ${parts.join(', ')}` : ''}
                    </li>
                  );
                })}
              </ul>
            </div>
          </HrnbAlert>
        )}

        <HrnbInput
          label="Название трека"
          value={trackTitle}
          onChange={(e) => setTrackTitle(e.target.value)}
          placeholder="Например: Мой фристайл"
          required
          disabled={loading}
        />

        <HrnbTextArea
          label="Описание"
          value={trackDescription}
          onChange={(e) => setTrackDescription(e.target.value)}
          placeholder="Необязательно"
          rows={3}
          disabled={loading}
        />

        <HrnbTextArea
          label="Текст трека"
          value={trackText}
          onChange={(e) => setTrackText(e.target.value)}
          placeholder="Введите текст трека (обязательно)"
          rows={5}
          required
          disabled={loading}
        />

        {uploadMode === 'file' ? (
          <HrnbFileUpload
            label="Аудиофайл"
            accept="audio/mpeg,audio/wav,audio/ogg,audio/flac,audio/aac,audio/mp4,.mp3,.wav,.ogg,.flac,.aac,.m4a"
            onChange={handleFileChange}
            value={selectedFile ? [selectedFile] : null}
            hint="Допустимые форматы: MP3, WAV, OGG, FLAC, AAC, M4A. Макс. размер: 30 МБ"
            disabled={loading}
          />
        ) : (
          <HrnbInput
            label="URL трека"
            value={trackUrl}
            onChange={(e) => setTrackUrl(e.target.value)}
            placeholder="https://example.com/track.mp3"
            required
            disabled={loading}
          />
        )}

        {/* Превью аудиофайла */}
        {trackType === 'audio' && previewUrl && uploadMode === 'file' && (
          <div className="track-uploader__preview">
            <AudioPlayer
              src={previewUrl}
              title={selectedFile?.name || 'Предпрослушивание'}
              showDownload={false}
            />
          </div>
        )}

        {/* Превью для URL */}
        {trackType === 'audio' && trackUrl && uploadMode === 'url' && (
          <div className="track-uploader__preview">
            <AudioPlayer
              src={trackUrl}
              title="Предпрослушивание"
              showDownload={false}
            />
          </div>
        )}

        {trackType === 'audio' && (
          <div className="track-uploader__row">
            <HrnbInput
              label="Длительность (сек)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="180"
              min="1"
              required
              disabled={loading}
              style={{ flex: 1 }}
            />

            <HrnbSelect
              label="Формат"
              value={format}
              onChange={(value) => setFormat(value)}
              options={audioFormats}
              disabled={loading}
              style={{ flex: 1 }}
            />
          </div>
        )}

        {loading && (
          <div className="track-uploader__progress">
            <HrnbLoader />
            <p>Загрузка трека... {progress > 0 && `(${progress}%)`}</p>

            {/* Прогресс бар */}
            {progress > 0 && (
              <div className="track-uploader__progress-bar">
                <div
                  className="track-uploader__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        <div className="track-uploader__actions">
          {onCancel && (
            <HrnbButton
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Отмена
            </HrnbButton>
          )}
          <HrnbButton
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Загрузка...' : trackType === 'text' ? 'Отправить текстовый трек' : uploadMode === 'file' ? 'Загрузить аудио трек' : 'Создать аудио трек'}
          </HrnbButton>
        </div>
      </form>
    </div>
  );
}
